import { MedplumClient, createReference } from '@medplum/core';
import {
  DiagnosticReport,
  Patient,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { ARKON } from '@/consts/systems.consts';
import {
  PreImplantationFormState,
  PreImplantationSaveParams,
  MEDICAL_HISTORY_REPORT_CATEGORIES,
} from './medical-history.types';
import {
  buildConclusionCode,
  getReportResultValueFromCode,
} from '../../files-tab/utils/report-files.utils';
import { ReportResultValue } from '../../files-tab/utils/report-files.consts';
import { flattenAnswers } from '@/utils/questionnaire-response.utils';
import { getQuestionText } from './questionnaire.utils';

// -----------------------------------------------------------------------------
// Initial Form State
// -----------------------------------------------------------------------------

export const createInitialPreImplantationFormState =
  (): PreImplantationFormState => ({
    type: '',
    date: '',
    result: null,
    observations: '',
    file: null,
  });

// -----------------------------------------------------------------------------
// Map DiagnosticReport to Form State
// -----------------------------------------------------------------------------

export const mapReportToFormState = (
  report: DiagnosticReport
): PreImplantationFormState => {
  const resultValue = getReportResultValueFromCode(
    report.conclusionCode?.[0]?.coding?.[0]?.code
  );

  return {
    type: report.code?.text || report.code?.coding?.[0]?.display || '',
    date: report.effectiveDateTime?.split('T')[0] || '',
    result: (resultValue as ReportResultValue | undefined) || null,
    observations: report.conclusion || '',
    file: null, // File is not retrieved from existing reports
  };
};

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

export const validatePreImplantationForm = (
  formState: PreImplantationFormState
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  if (!formState.type.trim()) {
    errors.type = 'Παρακαλώ εισάγετε το είδος του ελέγχου';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const isFormValid = (formState: PreImplantationFormState): boolean => {
  return formState.type.trim().length > 0;
};

// -----------------------------------------------------------------------------
// Conditional Field Helpers
// -----------------------------------------------------------------------------

export const shouldShowObservationsField = (
  result: ReportResultValue | null
): boolean => {
  return result === 'ABNORMAL';
};

// -----------------------------------------------------------------------------
// Build DiagnosticReport Payload
// -----------------------------------------------------------------------------

export const buildPreImplantationPayload = ({
  formState,
  patient,
  encounterId,
}: {
  formState: PreImplantationFormState;
  patient: Patient;
  encounterId?: string | null;
}): DiagnosticReport => {
  const effectiveDate =
    formState.date || new Date().toISOString().split('T')[0];

  return {
    resourceType: 'DiagnosticReport',
    status: 'final',
    category: [
      {
        coding: [
          {
            system: ARKON.CS.REPORTS.CATEGORY.BASE,
            code: MEDICAL_HISTORY_REPORT_CATEGORIES.PRE_IMPLANTATION_CHECK,
            display: 'Pre-implantation Check',
          },
        ],
        text: 'Προεμφυτευτικός έλεγχος',
      },
    ],
    code: {
      text: formState.type,
    },
    subject: createReference(patient),
    encounter: encounterId
      ? { reference: `Encounter/${encounterId}` }
      : undefined,
    effectiveDateTime: `${effectiveDate}T00:00:00.000Z`,
    conclusion: formState.observations || undefined,
    conclusionCode: buildConclusionCode(formState.result),
  };
};

// -----------------------------------------------------------------------------
// Save/Update/Delete Operations
// -----------------------------------------------------------------------------

export const savePreImplantationCheck = async ({
  medplum,
  patient,
  encounterId,
  formState,
  existingReport,
}: PreImplantationSaveParams): Promise<DiagnosticReport> => {
  const payload = buildPreImplantationPayload({
    formState,
    patient,
    encounterId,
  });

  let savedReport: DiagnosticReport;

  if (existingReport?.id) {
    savedReport = await medplum.updateResource({
      ...existingReport,
      ...payload,
      id: existingReport.id,
      resourceType: 'DiagnosticReport',
    });
  } else {
    savedReport = await medplum.createResource(payload);
  }

  // Upload file if provided
  if (formState.file && savedReport.id) {
    const binaryResource = await medplum.createBinary(
      formState.file,
      formState.file.name,
      formState.file.type
    );

    await medplum.updateResource({
      ...savedReport,
      presentedForm: [
        {
          contentType: 'application/pdf',
          url: `Binary/${binaryResource.id}`,
          title: formState.file.name,
        },
      ],
    });
  }

  return savedReport;
};

export const deletePreImplantationCheck = async (
  medplum: MedplumClient,
  report: DiagnosticReport
): Promise<void> => {
  if (!report.id) {
    return;
  }
  await medplum.deleteResource('DiagnosticReport', report.id);
};

// -----------------------------------------------------------------------------
// Fetch Pre-implantation Checks
// -----------------------------------------------------------------------------

export const fetchPreImplantationChecks = async (
  medplum: MedplumClient,
  patient: Patient
): Promise<DiagnosticReport[]> => {
  if (!patient?.id) {
    return [];
  }

  const categoryCode = `${ARKON.CS.REPORTS.CATEGORY.BASE}|${MEDICAL_HISTORY_REPORT_CATEGORIES.PRE_IMPLANTATION_CHECK}`;

  return medplum.searchResources('DiagnosticReport', {
    subject: `Patient/${patient.id}`,
    category: categoryCode,
    _sort: '-date',
  });
};

// -----------------------------------------------------------------------------
// Extract summary lines from questionnaire response
export const getSummaryLines = (
  periodType: 'prenatalPeriod' | 'perinatalPeriod',
  questionnaire: Questionnaire | undefined,
  questionnaireResponse: QuestionnaireResponse | null | undefined
): { label: string; value: string }[] => {
  const lines: { label: string; value: string }[] = [];

  // Flatten all answers from nested items into a flat object keyed by linkId
  const flattenedAnswers = flattenAnswers(questionnaireResponse?.item);

  // Helper to get answer value by linkId
  const getAnswer = (linkId: string) => {
    const value = flattenedAnswers[linkId];
    // Return the value directly, or undefined if not found
    return value !== undefined ? value : undefined;
  };

  // Helper to add a line for a question
  const addLine = (linkId: string, value: string | undefined) => {
    const label = getQuestionText(questionnaire, linkId);
    if (label) {
      lines.push({
        label,
        value: value || '-',
      });
    }
  };

  if (periodType === 'perinatalPeriod') {
    // Περιγεννητική περίοδος (Status)
    const status = getAnswer('perinatal-period');
    let statusValue = status || '-';
    if (status === 'άλλο') {
      const complications = getAnswer('complications');
      statusValue = complications ? `${status} (${complications})` : status;
    }
    addLine('perinatal-period', statusValue);

    // Τόκος (Birth number)
    const tokos = getAnswer('tokos');
    addLine('tokos', tokos?.toString());

    // Διάρκεια κύησης (Gestational duration)
    const duration = getAnswer('duration');
    addLine('duration', duration);

    // Δίδυμος Κύηση (Twin pregnancy)
    const twins = getAnswer('twins');
    const palidromisi = getAnswer('palidromisi');
    let twinValue = twins || '-';
    if (twins === 'Ναι' && palidromisi) {
      twinValue = 'Ναι (παλινδρόμηση)';
    }
    addLine('twins', twinValue);

    // Είδος τοκετού (Delivery type)
    const type = getAnswer('type');
    let deliveryValue = type || '-';
    if (type === 'Καισαρική τομή') {
      const reasonforkt = getAnswer('reasonforkt');
      deliveryValue = reasonforkt ? `${type} (${reasonforkt})` : type;
    }
    addLine('type', deliveryValue);

    // Κατάταξη (Classification)
    const classification = getAnswer('classification');
    addLine('classification', classification);

    // Σημειώσεις μαιευτηρίου (Maternity notes)
    const notes = getAnswer('notes');
    addLine('notes', notes);

    return lines;
  } else if (periodType === 'prenatalPeriod') {
    // Τρόπος σύλληψης (Conception method) - always show
    const conception = getAnswer('conception');
    addLine('conception', conception);

    // Conditional fields based on conception method
    if (conception === 'Υποβοηθούμενη αναπαραγωγή') {
      // Μέθοδος (Method)
      const method = getAnswer('method');
      addLine('method', method);

      // Πηγή βιολογικού υλικού (Source) - only if IVF
      if (method === 'IVF') {
        const source = getAnswer('source');
        addLine('source', source);
      }

      // Άλλο (Other) - only if method is Other
      if (method === 'Άλλο') {
        const other = getAnswer('other');
        addLine('other', other);
      }
    } else if (conception === 'Υιοθεσία') {
      // Ηλικία υιοθεσίας (Adoption age)
      const ageOfAdoption = getAnswer('age-of-adoption');
      const ageOfAdoptionValue = ageOfAdoption ? `${ageOfAdoption}` : undefined;
      addLine('age-of-adoption', ageOfAdoptionValue);

      // Συνθήκες διαβίωσης έως την υιοθεσία (Living conditions until adoption)
      const livingConditions = getAnswer('living-conditions');
      addLine('living-conditions', livingConditions);

      // Γνωστό ιστορικό βιολογικών συγγενών (Known biological family history)
      const knownHistory = getAnswer('known-history');
      const knownHistoryValue =
        knownHistory !== undefined ? (knownHistory ? '✓' : '✗') : undefined;
      addLine('known-history', knownHistoryValue);
    }

    return lines;
  }
  return lines;
};
